import { useCallback, useMemo, useState } from "react";

export type ColumnFilters = Record<string, Set<string>>;

export interface UseAdminColumnFiltersReturn {
  columnFilters: ColumnFilters;
  toggleFilter: (columnKey: string, value: string) => void;
  selectAllFilter: (columnKey: string, values: string[]) => void;
  clearFilter: (columnKey: string) => void;
  clearAllFilters: () => void;
  activeFilterCount: number;
}

export function useAdminColumnFilters(): UseAdminColumnFiltersReturn {
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});

  const toggleFilter = useCallback((columnKey: string, value: string) => {
    setColumnFilters((prev) => {
      const current = new Set(prev[columnKey] ?? []);
      if (current.has(value)) {
        current.delete(value);
      } else {
        current.add(value);
      }
      if (current.size === 0) {
        const { [columnKey]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [columnKey]: current };
    });
  }, []);

  const selectAllFilter = useCallback((columnKey: string, values: string[]) => {
    setColumnFilters((prev) => ({
      ...prev,
      [columnKey]: new Set(values),
    }));
  }, []);

  const clearFilter = useCallback((columnKey: string) => {
    setColumnFilters((prev) => {
      const { [columnKey]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const clearAllFilters = useCallback(() => {
    setColumnFilters({});
  }, []);

  const activeFilterCount = useMemo(
    () => Object.values(columnFilters).filter((s) => s.size > 0).length,
    [columnFilters]
  );

  return {
    columnFilters,
    toggleFilter,
    selectAllFilter,
    clearFilter,
    clearAllFilters,
    activeFilterCount,
  };
}
